import {
  getActorLikes,
  getFollows,
  getLikers,
  getProfile,
  resolveHandle,
  type Profile,
} from '../api/client'

/** Node in the social graph. `kind` records why the node is in the
 *  graph (the root user, a friend, a post they liked, or a co-liker
 *  who also liked one of their likes). */
export type NodeKind = 'root' | 'friend' | 'like' | 'coliker'

export interface SpiderNode {
  /** Stable id — DID for actors, post URI for liked posts. */
  id: string
  kind: NodeKind
  /** Handle (actors) or short post rkey (likes). */
  label: string
  /** Optional display label — full handle plus name for tooltip use. */
  displayName?: string
  avatar?: string
  /** Hop count from root. 0 = root, 1 = friend / like, 2 = co-liker. */
  depth: number
}

/** Edge `type` is the *reason* the connection exists. */
export type EdgeKind = 'follows' | 'liked' | 'co-liked'

export interface SpiderEdge {
  from: string
  to: string
  kind: EdgeKind
}

export interface BuildOptions {
  /** Cap on number of follows pulled (default 50). */
  maxFollows?: number
  /** Cap on number of liked posts pulled (default 30). */
  maxLikes?: number
  /** Cap on number of co-likers fetched PER liked post (default 8). */
  maxColikersPerPost?: number
  /** Progress callback for the UI. */
  onProgress?: (msg: string) => void
}

export interface SpiderGraph {
  root: string
  nodes: SpiderNode[]
  edges: SpiderEdge[]
  builtAt: string
}

/**
 * Build a social "spider web" graph rooted at one handle.
 *
 * Layers:
 *   • root   — the user
 *   • friend — everyone the user follows (capped)
 *   • like   — every post the user has liked (capped)
 *   • coliker — every other actor who also liked one of those posts
 *
 * Then emits JSON / Mermaid / an image-generation prompt. Built as a
 * class (not a free fn) so callers can hold the graph, re-render
 * different shapes, or extend with more layers without re-fetching.
 */
export class SpiderWeb {
  root: string
  rootDid: string | null = null
  rootProfile: Profile | null = null
  nodes: Map<string, SpiderNode> = new Map()
  edges: SpiderEdge[] = []
  builtAt: string | null = null

  constructor(rootHandle: string) {
    this.root = rootHandle.replace(/^@/, '').trim().toLowerCase()
  }

  private addNode(n: SpiderNode) {
    const cur = this.nodes.get(n.id)
    if (!cur || n.depth < cur.depth) {
      this.nodes.set(n.id, n)
    }
  }

  private addEdge(from: string, to: string, kind: EdgeKind) {
    // De-dup edges by (from, to, kind).
    const dup = this.edges.some(
      (e) => e.from === from && e.to === to && e.kind === kind,
    )
    if (!dup) this.edges.push({ from, to, kind })
  }

  /** Pull follows + likes + co-likers from the AppView, populating
   *  nodes + edges. Honours the per-layer caps to keep request count
   *  bounded (a 50/30/8 build = ~250 HTTP calls). */
  async buildFor(opts: BuildOptions = {}): Promise<SpiderGraph> {
    const maxFollows = opts.maxFollows ?? 50
    const maxLikes = opts.maxLikes ?? 30
    const maxColikers = opts.maxColikersPerPost ?? 8
    const log = opts.onProgress ?? (() => {})

    log(`resolving @${this.root}…`)
    this.rootDid = await resolveHandle(this.root)
    this.rootProfile = await getProfile(this.rootDid)
    this.addNode({
      id: this.rootDid,
      kind: 'root',
      label: this.rootProfile.handle,
      displayName: this.rootProfile.displayName,
      avatar: this.rootProfile.avatar,
      depth: 0,
    })

    log(`fetching up to ${maxFollows} follows…`)
    const follows = (await getFollows(this.rootDid, maxFollows)).slice(0, maxFollows)
    for (const f of follows) {
      this.addNode({
        id: f.did,
        kind: 'friend',
        label: f.handle,
        displayName: f.displayName,
        avatar: f.avatar,
        depth: 1,
      })
      this.addEdge(this.rootDid, f.did, 'follows')
    }

    log(`fetching up to ${maxLikes} likes…`)
    const likes = (await getActorLikes(this.root, maxLikes)).slice(0, maxLikes)
    for (const l of likes) {
      const p = l.post
      this.addNode({
        id: p.uri,
        kind: 'like',
        label: p.author.handle,
        displayName: p.record?.text?.slice(0, 60),
        depth: 1,
      })
      this.addEdge(this.rootDid, p.uri, 'liked')
      // Also surface the post author as a friend-tier node — they're
      // someone the user engages with even if they don't follow.
      this.addNode({
        id: p.author.did,
        kind: 'friend',
        label: p.author.handle,
        displayName: p.author.displayName,
        avatar: p.author.avatar,
        depth: 1,
      })
      this.addEdge(p.author.did, p.uri, 'follows')
    }

    log(`fetching co-likers (${maxColikers}/post × ${likes.length})…`)
    let postIdx = 0
    for (const l of likes) {
      postIdx += 1
      const p = l.post
      try {
        const others = await getLikers(p.uri, maxColikers)
        for (const o of others) {
          if (o.actor.did === this.rootDid) continue
          this.addNode({
            id: o.actor.did,
            kind: 'coliker',
            label: o.actor.handle,
            displayName: o.actor.displayName,
            avatar: o.actor.avatar,
            depth: 2,
          })
          this.addEdge(o.actor.did, p.uri, 'co-liked')
        }
      } catch (e) {
        log(`  co-likers ${postIdx}/${likes.length} failed: ${(e as Error).message}`)
      }
    }

    this.builtAt = new Date().toISOString()
    log(`done — ${this.nodes.size} nodes, ${this.edges.length} edges`)
    return this.toJSON()
  }

  toJSON(): SpiderGraph {
    return {
      root: this.rootDid ?? this.root,
      nodes: Array.from(this.nodes.values()),
      edges: this.edges,
      builtAt: this.builtAt ?? new Date().toISOString(),
    }
  }

  /** Produce a Mermaid `graph` diagram. Edges are styled by kind so a
   *  Mermaid renderer (and any AI looking at the source) can see the
   *  spider-web layering: solid for follows, dashed for likes, dotted
   *  for co-likes. */
  toMermaid(): string {
    if (!this.rootDid) {
      return 'graph LR\n  empty["build the graph first"]'
    }
    const lines: string[] = ['graph LR']
    const idFor = makeShortIdFactory()
    const labelFor = (n: SpiderNode) =>
      n.kind === 'like'
        ? `📝 ${n.label}`
        : n.kind === 'root'
          ? `★ @${n.label}`
          : n.kind === 'friend'
            ? `@${n.label}`
            : `@${n.label}`

    // classDef per kind so the renderer (and AI) gets visual hints.
    lines.push('  classDef root fill:#0c4a6e,stroke:#38bdf8,color:#fff,stroke-width:2px;')
    lines.push('  classDef friend fill:#1e293b,stroke:#22d3ee,color:#e0f2fe;')
    lines.push('  classDef like fill:#3b0764,stroke:#a855f7,color:#f5d0fe;')
    lines.push('  classDef coliker fill:#1f2937,stroke:#475569,color:#cbd5e1;')

    for (const n of this.nodes.values()) {
      const id = idFor(n.id)
      // Escape quotes in labels for Mermaid.
      const safe = labelFor(n).replace(/"/g, "'")
      lines.push(`  ${id}["${safe}"]:::${n.kind}`)
    }

    for (const e of this.edges) {
      const a = idFor(e.from)
      const b = idFor(e.to)
      const arrow =
        e.kind === 'follows' ? '-->' : e.kind === 'liked' ? '-..->' : '-.->'
      lines.push(`  ${a} ${arrow} ${b}`)
    }

    return lines.join('\n')
  }

  /** Image-gen prompt that captures the topology so a model can render
   *  the graph as an artistic spider web. We feed the mermaid in too
   *  because some models (e.g. ChatGPT with code interpreter) render
   *  it cleanly. */
  toAiPrompt(): string {
    const counts = this.countsByKind()
    const friends = Array.from(this.nodes.values()).filter((n) => n.kind === 'friend')
    const colikers = Array.from(this.nodes.values()).filter((n) => n.kind === 'coliker')
    const sampleFriends = friends.slice(0, 12).map((n) => `@${n.label}`)
    const sampleColikers = colikers.slice(0, 12).map((n) => `@${n.label}`)
    return [
      `Generate an artistic spider-web visualization of a Bluesky social graph.`,
      `Aesthetic: dark cosmic background, silken radial threads, glowing nodes for handles, organic curved web filaments, subtle bioluminescence on the strongest connections.`,
      ``,
      `Center node (★): @${this.root}`,
      `Inner ring — ${counts.friend} friends (people they follow / post authors): ${sampleFriends.join(', ')}${friends.length > sampleFriends.length ? ` … +${friends.length - sampleFriends.length} more` : ''}`,
      `Mid ring — ${counts.like} liked posts radiating off the center.`,
      `Outer ring — ${counts.coliker} co-likers (people who liked the same posts): ${sampleColikers.join(', ')}${colikers.length > sampleColikers.length ? ` … +${colikers.length - sampleColikers.length} more` : ''}`,
      ``,
      `Use thicker solid threads for "follows" edges, glowing dashed strands for "liked" edges, and faint dotted spokes for "co-liked" edges.`,
      `Lay handles around the perimeter; let the threads curve to form an obvious spider-web silhouette.`,
      ``,
      `If your tool supports it, render the underlying Mermaid graph below directly:`,
      '```mermaid',
      this.toMermaid(),
      '```',
    ].join('\n')
  }

  countsByKind(): Record<NodeKind, number> {
    const c: Record<NodeKind, number> = { root: 0, friend: 0, like: 0, coliker: 0 }
    for (const n of this.nodes.values()) c[n.kind] += 1
    return c
  }
}

/** Mermaid identifiers must be `[A-Za-z_][\w]*`. DIDs and at:// URIs
 *  break that, so we map every distinct source-id to a short token
 *  (`n0`, `n1`, …). Single closure per Mermaid render — re-runs get a
 *  fresh, stable mapping. */
function makeShortIdFactory(): (raw: string) => string {
  const seen = new Map<string, string>()
  return (raw) => {
    const cur = seen.get(raw)
    if (cur) return cur
    const id = `n${seen.size}`
    seen.set(raw, id)
    return id
  }
}
